/**
 * Zoho Form Drawer Handler
 * Manages opening and closing of Zoho form drawer
 * Shows/hides pre-rendered Zoho form snippets based on button clicks
 * Also populates existing #leading-time and #shipping-time elements
 * 
 * FORM SNIPPETS SETUP:
 * All Zoho forms are pre-rendered in zoho-form-drawer.liquid as hidden divs
 * Each form has a data-form-snippet attribute matching the Liquid file name
 * Buttons trigger the drawer with data-zoho-form-snippet attribute
 * 
 * STATE TO FORM MAPPING:
 * - State 1 (Default + Customizable): zoho-customize-your-piece
 * - State 2 (Made to Order): zoho-made-to-order
 * - State 3 (One-off): zoho-begin-your-project
 * - State 4 (Vintage Sold): zoho-vintage-request-form
 * - State 5 (Request): zoho-connect-with-serpentine
 * - Sold out States 1-3: zoho-connect-with-serpentine
 */

class ZohoFormDrawer {
  constructor() {
    this.drawer = document.getElementById('zoho-form-drawer');
    this.closeButton = document.getElementById('zoho-drawer-close');
    this.formContainer = document.getElementById('zoho-form-container');
    this.isOpen = false;
    this.currentFormSnippet = null;
    
    this.init();
  }
  
  init() {
    // Populate leadtime and shipping messages from product data
    this.populateProductMessages();
    
    if (!this.drawer) return;
    
    // Bind close events
    this.closeButton?.addEventListener('click', () => this.close());
    
    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
    
    // Close on backdrop click
    this.drawer.addEventListener('click', (e) => {
      if (e.target === this.drawer) {
        this.close();
      }
    });
    
    // Listen for button clicks
    this.attachButtonListeners();
    
    // Handle request customization button
    this.handleRequestCustomizationButton();

    // Open the submitted form after Zoho redirects back to the PDP
    this.handleReturnState();
  }
  
  handleRequestCustomizationButton() {
    const requestCustomizationBtn = document.getElementById('request-customization');
    if (!requestCustomizationBtn) return;
    
    const customizeButton = document.getElementById('customize-button');
    
    if (!customizeButton) {
      // Find the highest parent .product__block
      let parentBlock = null;
      let element = requestCustomizationBtn;
      
      while (element.parentElement) {
        element = element.parentElement;
        if (element.classList.contains('product__block')) {
          parentBlock = element;
        }
      }
      
      if (parentBlock) {
        parentBlock.style.display = 'none';
        
        // Check if the previous sibling is a divider and hide it too
        const previousElement = parentBlock.previousElementSibling;
        if (previousElement && previousElement.classList.contains('product__block--divider')) {
          previousElement.style.display = 'none';
        }
      }
      return;
    }
    
    // Add click handler to trigger customize button
    requestCustomizationBtn.addEventListener('click', (e) => {
      e.preventDefault();
      customizeButton.click();
    });
  }
  
  populateProductMessages() {
    // Find the product state data
    const productStateDataEl = document.getElementById('product-state-data');
    if (!productStateDataEl) return;
    
    try {
      const productData = JSON.parse(productStateDataEl.textContent);
      
      // Find and populate #leading-time element (if it exists in the DOM)
      const leadingTimeEl = document.getElementById('leading-time');
      if (leadingTimeEl) {
        if (productData.leadingTime) {
          leadingTimeEl.textContent = productData.leadingTime;
        } else {
          // Hide the highest parent .product__block if leading time is empty
          this.hideEmptyBlock(leadingTimeEl);
        }
      }
      
      // Find and populate #shipping-time element (if it exists in the DOM)
      const shippingTimeEl = document.getElementById('shipping-time');
      if (shippingTimeEl) {
        if (productData.shippingTime) {
          shippingTimeEl.textContent = productData.shippingTime;
        } else {
          // Hide the highest parent .product__block if shipping time is empty
          this.hideEmptyBlock(shippingTimeEl);
        }
      }
    } catch (error) {
      console.error('Error parsing product state data:', error);
    }
  }
  
  hideEmptyBlock(element) {
    // Find the highest parent .product__block
    let parentBlock = null;
    let el = element;
    
    while (el.parentElement) {
      el = el.parentElement;
      if (el.classList.contains('product__block')) {
        parentBlock = el;
      }
    }
    
    if (parentBlock) {
      parentBlock.style.display = 'none';
      
      // Check if the previous sibling is a divider and hide it too
      const previousElement = parentBlock.previousElementSibling;
      if (previousElement && previousElement.classList.contains('product__block--divider')) {
        previousElement.style.display = 'none';
      }
    }
  }
  
  attachButtonListeners() {
    // Listen for all clicks on buttons with data-zoho-form-snippet
    document.addEventListener('click', (e) => {
      const target = e.target.closest('[data-zoho-form-snippet]');
      if (!target) return;
      
      const formSnippet = target.getAttribute('data-zoho-form-snippet');
      const productTitle = target.getAttribute('data-product-title') || '';
      
      if (formSnippet) {
        e.preventDefault();
        this.open(formSnippet, productTitle);
      }
    });
  }
  
  open(formSnippet, productTitle = '') {
    if (!this.drawer || !formSnippet) return;
    
    // Hide all forms first
    this.hideAllForms();
    
    // Show the requested form
    const formWrapper = this.formContainer.querySelector(`[data-form-snippet="${formSnippet}"]`);
    if (!formWrapper) {
      console.warn('Form snippet not found:', formSnippet);
      return;
    }
    
    formWrapper.style.display = 'block';
    this.currentFormSnippet = formSnippet;

    window.SerpentineZohoForms?.resetThankYou(this.formContainer);
    window.SerpentineZohoForms?.setRedirectValues(formWrapper);
    
    // Pre-fill product title if there's a field for it
    this.prefillProductTitle(formWrapper, productTitle);
    
    // Open drawer
    this.drawer.removeAttribute('inert');
    this.drawer.removeAttribute('aria-hidden');
    
    if (typeof this.drawer.showModal === 'function') {
      this.drawer.showModal();
    } else {
      this.drawer.setAttribute('open', '');
    }
    
    this.isOpen = true;
    
    // Lock scroll
    document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));
    
    // Trap focus
    this.trapFocus();
  }

  handleReturnState() {
    const zohoForms = window.SerpentineZohoForms;

    if (!zohoForms?.returnState?.formFilled) return;

    const formSnippet = zohoForms.returnState.formSnippet || this.formContainer.querySelector('.zoho-form-wrapper')?.getAttribute('data-form-snippet');

    if (!formSnippet) return;

    this.open(formSnippet);

    const formWrapper = this.formContainer.querySelector(`[data-form-snippet="${formSnippet}"]`);
    if (formWrapper) {
      zohoForms.showThankYou(formWrapper);
    }

    zohoForms.cleanUrl();
  }
  
  close() {
    if (!this.drawer || !this.isOpen) return;
    
    this.drawer.setAttribute('closing', '');
    
    // Wait for animation
    setTimeout(() => {
      if (typeof this.drawer.close === 'function') {
        this.drawer.close();
      } else {
        this.drawer.removeAttribute('open');
      }
      
      this.drawer.setAttribute('aria-hidden', 'true');
      this.drawer.setAttribute('inert', '');
      this.drawer.removeAttribute('closing');
      this.isOpen = false;
      this.currentFormSnippet = null;
      
      // Hide all forms
      this.hideAllForms();
      
      // Unlock scroll
      if (!window.theme?.hasOpenModals?.()) {
        document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
      }
      
      // Remove focus trap
      this.removeFocusTrap();
    }, 300); // Adjust timing to match CSS transition
  }
  
  hideAllForms() {
    if (!this.formContainer) return;
    
    const allForms = this.formContainer.querySelectorAll('.zoho-form-wrapper');
    allForms.forEach(form => {
      form.style.display = 'none';
    });
  }
  
  prefillProductTitle(formWrapper, productTitle) {
    if (!productTitle) return;
    
    // Try to find common field names that might contain product title
    // Adjust these selectors based on your actual Zoho form field names
    const possibleSelectors = [
      'input[name*="Product"]',
      'input[name*="product"]',
      'input[name="SingleLine"]',
      'input[name="SingleLine1"]',
      'textarea[name*="Product"]',
      'textarea[name*="product"]'
    ];
    
    for (const selector of possibleSelectors) {
      const field = formWrapper.querySelector(selector);
      if (field && !field.value) {
        field.value = productTitle;
        break;
      }
    }
  }
  
  trapFocus() {
    // Simple focus trap
    const focusableElements = this.drawer.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length === 0) return;
    
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    
    this.focusTrapHandler = (e) => {
      if (e.key !== 'Tab') return;
      
      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    };
    
    this.drawer.addEventListener('keydown', this.focusTrapHandler);
    
    // Focus first element
    setTimeout(() => {
      if (firstElement && typeof firstElement.focus === 'function') {
        firstElement.focus();
      }
    }, 100);
  }
  
  removeFocusTrap() {
    if (this.focusTrapHandler) {
      this.drawer.removeEventListener('keydown', this.focusTrapHandler);
      this.focusTrapHandler = null;
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ZohoFormDrawer();
  });
} else {
  new ZohoFormDrawer();
}
